import React, { useEffect, useRef, useState } from "react";
import {
  Check,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  LoaderCircle,
} from "lucide-react";
import "./publicContactEnquiry.css";
import {
  PUBLIC_BOOKING_PHONE_COUNTRIES,
  normalizePublicBookingNationalNumber,
} from "./publicBookingPhone";

export function PublicContactHoneypot({ value = "", onChange }) {
  return (
    <div className="pv-contact-honeypot" aria-hidden="true">
      <label htmlFor="pv-contact-company">Company</label>
      <input
        id="pv-contact-company"
        name="company"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

const getPublicContactPhoneCountry = (countryCode) =>
  PUBLIC_BOOKING_PHONE_COUNTRIES.find(
    (country) => country.code === String(countryCode || "").trim(),
  ) || null;

const getNationalPhonePlaceholder = (value) => {
  const stripped = String(value || "")
    .replace(/^\s*\+\d{1,4}[\s().-]*/, "")
    .trim();

  return stripped || "98XXXXXXXX";
};

const PublicContactCountryFlag = ({ code }) => {
  const normalized = String(code || "").toUpperCase();

  if (normalized === "IN") {
    return (
      <svg className="pv-contact-phone-country-flag" viewBox="0 0 24 16" aria-hidden="true" focusable="false">
        <rect width="24" height="16" rx="2" fill="#ffffff" />
        <path d="M0 0h24v5.33H0z" fill="#ff9933" />
        <path d="M0 10.67h24V16H0z" fill="#138808" />
        <circle cx="12" cy="8" r="1.7" fill="none" stroke="#000080" strokeWidth="0.7" />
        <circle cx="12" cy="8" r="0.45" fill="#000080" />
      </svg>
    );
  }

  if (normalized === "NP") {
    return (
      <svg className="pv-contact-phone-country-flag" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <path d="M3 1.5v17h13L7.4 11.2h7.7L3 1.5Z" fill="#dc143c" stroke="#003893" strokeWidth="1.2" strokeLinejoin="round" />
        <circle cx="6.1" cy="7.2" r="1.2" fill="#ffffff" />
        <circle cx="7.2" cy="14.2" r="1.35" fill="#ffffff" />
      </svg>
    );
  }

  if (normalized === "SA") {
    return (
      <svg className="pv-contact-phone-country-flag" viewBox="0 0 24 16" aria-hidden="true" focusable="false">
        <rect width="24" height="16" rx="2" fill="#006c35" />
        <path d="M6.2 6.2h11.6M8 8h8" stroke="#ffffff" strokeWidth="1.1" strokeLinecap="round" />
        <path d="M7 11.5h10.2" stroke="#ffffff" strokeWidth="0.9" strokeLinecap="round" />
      </svg>
    );
  }

  if (normalized === "AE") {
    return (
      <svg className="pv-contact-phone-country-flag" viewBox="0 0 24 16" aria-hidden="true" focusable="false">
        <rect width="24" height="16" rx="2" fill="#ffffff" />
        <path d="M0 0h24v5.33H0z" fill="#00732f" />
        <path d="M0 10.67h24V16H0z" fill="#000000" />
        <path d="M0 0h6.3v16H0z" fill="#ff0000" />
      </svg>
    );
  }

  return (
    <span
      className="pv-contact-phone-country-flag pv-contact-phone-country-flag-fallback"
      aria-hidden="true"
    />
  );
};

export function PublicContactPhoneField({
  id,
  value = "",
  countryCode = "",
  onChange,
  inputClassName = "",
  placeholder = "98XXXXXXXX",
  required = false,
}) {
  const selectedCountry =
    getPublicContactPhoneCountry(countryCode) ||
    PUBLIC_BOOKING_PHONE_COUNTRIES[0];
  const [countryOpen, setCountryOpen] = useState(false);
  const countryRef = useRef(null);

  useEffect(() => {
    if (!countryOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!countryRef.current?.contains(event.target)) {
        setCountryOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setCountryOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [countryOpen]);

  const emitChange = (name, nextValue) => {
    onChange?.({ target: { name, value: nextValue } });
  };

  const handlePhoneChange = (event) => {
    const rawValue = event?.target?.value ?? "";
    let nextCountryCode = selectedCountry.code;

    if (String(rawValue).trim().startsWith("+")) {
      const phoneDigits = String(rawValue).replace(/\D+/g, "");
      const detectedCountry = PUBLIC_BOOKING_PHONE_COUNTRIES.find((country) =>
        phoneDigits.startsWith(String(country.dialCode).replace(/\D+/g, "")),
      );

      if (detectedCountry) {
        nextCountryCode = detectedCountry.code;
        emitChange("phoneCountryCode", detectedCountry.code);
      }
    }

    emitChange(
      "phone",
      normalizePublicBookingNationalNumber(rawValue, nextCountryCode),
    );
  };

  return (
    <div className="pv-contact-phone-field">
      <div
        className="pv-contact-phone-country-select"
        ref={countryRef}
      >
        <button
          type="button"
          className="pv-contact-phone-country-trigger"
          onClick={() => setCountryOpen((current) => !current)}
          aria-haspopup="listbox"
          aria-expanded={countryOpen}
          aria-label="Country code"
        >
          <PublicContactCountryFlag code={selectedCountry.code} />
          <strong>{selectedCountry.dialCode}</strong>
          <ChevronDown
            size={16}
            strokeWidth={2.2}
            aria-hidden="true"
          />
        </button>

        {countryOpen && (
          <div
            className="pv-contact-phone-country-menu"
            role="listbox"
            aria-label="Country code"
          >
            {PUBLIC_BOOKING_PHONE_COUNTRIES.map((country) => {
              const isSelected = country.code === selectedCountry.code;

              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={isSelected ? "is-selected" : ""}
                  key={country.code}
                  onClick={() => {
                    emitChange("phoneCountryCode", country.code);
                    setCountryOpen(false);
                  }}
                >
                  <PublicContactCountryFlag code={country.code} />
                  <span>{country.dialCode}</span>
                  {isSelected ? (
                    <Check
                      size={15}
                      strokeWidth={2.4}
                      aria-hidden="true"
                    />
                  ) : (
                    <span aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <input
        id={id}
        className={`pv-contact-phone-number ${inputClassName}`.trim()}
        name="phone"
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        maxLength={15}
        placeholder={getNationalPhonePlaceholder(placeholder)}
        value={value}
        onChange={handlePhoneChange}
        required={required}
      />
    </div>
  );
}

export default function PublicContactEnquiryStatus({ submitting, feedback, comfortableSpacing = false }) {
  if (!submitting && !feedback) return null;

  if (submitting) {
    return (
      <div className={`pv-contact-status is-loading${comfortableSpacing ? " is-comfortable" : ""}`} role="status" aria-live="polite">
        <LoaderCircle className="pv-contact-status__icon is-spinning" aria-hidden="true" />
        <span>Sending your message securely…</span>
      </div>
    );
  }

  const isSuccess = feedback?.type === "success";

  return (
    <div
      className={`pv-contact-status ${isSuccess ? "is-success" : "is-error"}${comfortableSpacing ? " is-comfortable" : ""}`}
      role={isSuccess ? "status" : "alert"}
      aria-live="polite"
    >
      {isSuccess ? (
        <CheckCircle2 className="pv-contact-status__icon" aria-hidden="true" />
      ) : (
        <CircleAlert className="pv-contact-status__icon" aria-hidden="true" />
      )}
      <span>{feedback?.message}</span>
    </div>
  );
}
